import { useEffect, useRef } from "react"

/**
 * Unsaved forms kept on the device (14 Oct 2026: Ilda left HQ to copy the links of a big custom order,
 * the iPhone reloaded HQ in the background, and everything she had typed was gone). A form's value is
 * written to localStorage as it changes, and read back when the screen opens again; a successful save
 * clears it. Photos already uploaded are kept as their addresses.
 *
 * Every access is wrapped: in a private window, or with storage blocked, the form simply works as before.
 */

const PREFIX = "hq-draft:"
/** Fired on window whenever a draft is written or cleared (the open-orders list follows it). */
export const DRAFTS_CHANGED = "hq-drafts-changed"
const announce = () => {
  try {
    window.dispatchEvent(new Event(DRAFTS_CHANGED))
  } catch {
    // no window (tests)
  }
}
/** A draft older than this is not offered back. */
export const DRAFT_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000

export type StoredDraft<T> = { value: T; saved_at: number; extra?: Record<string, string> }

export function readDraft<T>(key: string, now = Date.now()): StoredDraft<T> | null {
  try {
    const raw = window.localStorage.getItem(PREFIX + key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredDraft<T>
    if (!parsed || typeof parsed.saved_at !== "number" || now - parsed.saved_at > DRAFT_MAX_AGE_MS) {
      window.localStorage.removeItem(PREFIX + key)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function writeDraft<T>(key: string, value: T, extra?: Record<string, string>) {
  try {
    window.localStorage.setItem(PREFIX + key, JSON.stringify({ value, saved_at: Date.now(), ...(extra ? { extra } : {}) }))
    announce()
  } catch {
    // Storage full or blocked: the form still works, it just is not kept.
  }
}

export function clearDraft(key: string) {
  try {
    window.localStorage.removeItem(PREFIX + key)
    announce()
  } catch {
    // nothing to do
  }
}

/**
 * Keeps `value` under `key` while it differs from `pristine` (what the form opened with), and drops
 * the draft when it is back to pristine. `key` null = not keeping (e.g. the form is not ready yet).
 * Written at once on every change — also when the page is hidden, which is what iOS does before it
 * throws a backgrounded page away.
 */
export function useDraftKeeper<T>(key: string | null, value: T | null, pristine: T | null, extra?: Record<string, string>) {
  const latest = useRef({ key, value, pristine, extra })
  latest.current = { key, value, pristine, extra }
  useEffect(() => {
    if (!key || value == null) return
    if (pristine != null && JSON.stringify(value) === JSON.stringify(pristine)) clearDraft(key)
    else writeDraft(key, value, extra)
  }, [key, value, pristine, extra])
  useEffect(() => {
    const flush = () => {
      const { key: k, value: v, pristine: p, extra: e } = latest.current
      if (k && v != null && !(p != null && JSON.stringify(v) === JSON.stringify(p))) writeDraft(k, v, e)
    }
    document.addEventListener("visibilitychange", flush)
    window.addEventListener("pagehide", flush)
    return () => {
      document.removeEventListener("visibilitychange", flush)
      window.removeEventListener("pagehide", flush)
    }
  }, [])
}
